<template>
    <div
        class="absolute bottom-22 right-3 sm:right-6 z-20 hidden sm:block bg-white/70 backdrop-blur-sm duration-200 ease-out rounded-lg shadow overflow-hidden"
        :class="{
            'w-64': !collapsed,
            'w-10': collapsed,
        }"
    >
        <!-- Header -->
        <div
            class="flex items-center justify-between p-2 border-b border-gray-200/50"
        >
            <h3
                v-show="!collapsed"
                class="text-sm font-semibold text-gray-800 text-nowrap"
            >
                Ovládání počasí
            </h3>
            <button
                class="flex items-center justify-center w-6 h-6 rounded-md hover:bg-gray-100/70 transition-colors"
                @click="collapsed = !collapsed"
                :title="
                    collapsed
                        ? 'Rozbalit ovládání počasí'
                        : 'Sbalit ovládání počasí'
                "
            >
                <span
                    class="esri-icon transition-transform duration-200 text-lg!"
                    :class="{
                        'esri-icon-left-triangle-arrow': collapsed,
                        'esri-icon-right-triangle-arrow': !collapsed,
                    }"
                ></span>
            </button>
        </div>

        <!-- Viewpoints Grid -->
        <div
            v-show="!collapsed"
            class="p-3 space-y-1 max-h-80 overflow-y-auto"
        >
            <!-- Viewpoints List -->
            <div
                class="flex justify-evenly items-center w-full border-b border-gray-200/50 pb-3"
            >
                <template
                    v-for="item in weatherTypes"
                    :key="item.name"
                >
                    <button
                        v-if="item.icon"
                        class="cursor-pointer bg-white/70 backdrop-blur-sm shadow rounded-lg p-2 sm:flex justify-center items-center hover:bg-gray-100 size-8"
                        @click="weather = item.name"
                    >
                        <span
                            class="esri-icon"
                            :class="item.icon"
                        />
                    </button>
                    <button
                        v-else-if="item.img"
                        class="cursor-pointer bg-white/70 backdrop-blur-sm shadow rounded-lg p-2 sm:flex justify-center items-center hover:bg-gray-100 size-8"
                        @click="weather = item.name"
                    >
                        <img
                            :src="item.img"
                            class="size-4"
                        />
                    </button>
                </template>
            </div>

            <div class="weather-sliders py-3 flex flex-col gap-3 text-sm">
                <div
                    v-show="weather && weather !== 'foggy'"
                    class="weather-slider-label text-nowrap"
                >
                    Oblačnost
                </div>
                <div
                    v-show="weather && weather !== 'foggy'"
                    ref="cloudCoverSliderEl"
                    class="slider"
                />
                <div
                    v-show="weather === 'foggy'"
                    class="weather-slider-label text-nowrap"
                >
                    Hustota mlhy
                </div>
                <div
                    v-show="weather === 'foggy'"
                    ref="fogStrengthSliderEl"
                    class="slider"
                />
                <div
                    v-show="weather === 'rainy' || weather === 'snowy'"
                    class="weather-slider-label text-nowrap"
                >
                    Srážky
                </div>
                <div
                    v-show="weather === 'rainy' || weather === 'snowy'"
                    ref="precipitationSliderEl"
                    class="slider"
                />
                <div
                    v-show="weather === 'snowy'"
                    class="flex items-center gap-2"
                >
                    <input
                        v-if="weather === 'snowy'"
                        id="snowCoverCheck"
                        type="checkbox"
                        v-model="snowCover"
                        true-value="enabled"
                        false-value="disabled"
                    />
                    <label for="snowCoverCheck">Sněhová pokřývka</label>
                </div>
            </div>
        </div>

        <!-- Collapsed State Icon -->
        <div
            v-show="collapsed"
            class="p-2 flex justify-center"
            title="Rozbalit ovládání počasí"
            @click="collapsed = !collapsed"
        >
            <span class="esri-icon esri-icon-partly-cloudy text-lg!"></span>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from "vue"
import { useUtils } from "../utils/utils"
import { useStore } from "../store"

import Slider from "@arcgis/core/widgets/Slider"
import SunnyWeather from "@arcgis/core/views/3d/environment/SunnyWeather"
import CloudyWeather from "@arcgis/core/views/3d/environment/CloudyWeather"
import RainyWeather from "@arcgis/core/views/3d/environment/RainyWeather"
import SnowyWeather from "@arcgis/core/views/3d/environment/SnowyWeather"
import FoggyWeather from "@arcgis/core/views/3d/environment/FoggyWeather"

const cloudCoverSliderEl = ref<HTMLDivElement | null>(null)
let sliderCloudCover: Slider | null = null

const fogStrengthSliderEl = ref<HTMLDivElement | null>(null)
let sliderFogStrength: Slider | null = null

const precipitationSliderEl = ref<HTMLDivElement | null>(null)
let sliderPrecipitation: Slider | null = null

const { getView, getViewRight } = useUtils()
const store = useStore()

const view = getView()

const collapsed = ref(true)

const weather = ref<WeatherType>("sunny")

type WeatherType = "sunny" | "cloudy" | "rainy" | "snowy" | "foggy"
const weatherTypes: { name: WeatherType; icon?: string; img?: string }[] = [
    {
        name: "sunny",
        icon: "esri-icon-environment-settings",
    },
    {
        name: "cloudy",
        icon: "esri-icon-partly-cloudy",
    },
    {
        name: "rainy",
        img: "./img/rainy.svg",
    },
    {
        name: "snowy",
        img: "./img/snowy.svg",
    },
    {
        name: "foggy",
        img: "./img/foggy.svg",
    },
]

if (view.environment.weather?.type !== undefined) {
    weather.value = view.environment.weather?.type as WeatherType
}
const cloudCover = ref(0.2)
const fogStrength = ref(0.4)
const precipitation = ref(0.3)
const snowCover = ref<"enabled" | "disabled">("disabled")

watch(
    () => store.isMeshLayerActivated,
    newVal => {
        if (newVal) {
            let interval = setInterval(() => {
                const sceneMesh = document.querySelector(
                    ".scene-mesh",
                ) as HTMLArcgisSceneElement
                if (!sceneMesh) return
                clearInterval(interval)
                setWeather(weather.value)
            }, 50)
        }
    },
)

watch(weather, newValue => {
    setWeather(newValue)
})

watch(snowCover, newValue => {
    setWeatherProperty("snowCover", newValue)
})

onMounted(() => {
    const weather = view.environment.weather?.type
    if (weather === "cloudy") cloudCover.value = 0.3
    if (weather === "snowy") cloudCover.value = 0.7

    if (
        cloudCoverSliderEl.value &&
        fogStrengthSliderEl.value &&
        precipitationSliderEl.value
    ) {
        sliderCloudCover = new Slider({
            container: cloudCoverSliderEl.value,
            min: 0,
            max: 1,
            steps: 0.1,
            values: [cloudCover.value],
        })
        sliderCloudCover.on("thumb-drag", e => {
            if (e.state != "start") {
                cloudCover.value = e.value
                setWeatherProperty("cloudCover", cloudCover.value)
            }
        })

        sliderFogStrength = new Slider({
            container: fogStrengthSliderEl.value,
            min: 0,
            max: 1,
            steps: 0.1,
            values: [fogStrength.value],
        })
        sliderFogStrength.on("thumb-drag", e => {
            if (e.state != "start") {
                fogStrength.value = e.value
                setWeatherProperty("fogStrength", fogStrength.value)
            }
        })

        sliderPrecipitation = new Slider({
            container: precipitationSliderEl.value,
            min: 0,
            max: 1,
            steps: 0.1,
            values: [precipitation.value],
        })
        sliderPrecipitation.on("thumb-drag", e => {
            if (e.state != "start") {
                precipitation.value = e.value
                setWeatherProperty("precipitation", precipitation.value)
            }
        })
    }
})

function setWeather(weatherName: WeatherType) {
    const viewRight = getViewRight()
    if (sliderCloudCover && sliderFogStrength && sliderPrecipitation) {
        let newWeather:
            | SunnyWeather
            | CloudyWeather
            | RainyWeather
            | SnowyWeather
            | FoggyWeather
        switch (weatherName) {
            case "sunny":
                cloudCover.value = 0.2
                sliderCloudCover.values = [cloudCover.value]
                newWeather = new SunnyWeather({
                    cloudCover: cloudCover.value,
                })
                view.environment.weather = newWeather
                if (viewRight) {
                    viewRight.environment.weather = newWeather
                }
                break
            case "cloudy":
                cloudCover.value = 0.3
                sliderCloudCover.values = [cloudCover.value]
                newWeather = new CloudyWeather({
                    cloudCover: cloudCover.value,
                })
                view.environment.weather = newWeather
                if (viewRight) {
                    viewRight.environment.weather = newWeather
                }
                break
            case "rainy":
                cloudCover.value = 0.2
                sliderCloudCover.values = [cloudCover.value]
                precipitation.value = 0.3
                sliderPrecipitation.values = [precipitation.value]
                newWeather = new RainyWeather({
                    cloudCover: cloudCover.value,
                    precipitation: precipitation.value,
                })
                view.environment.weather = newWeather
                if (viewRight) {
                    viewRight.environment.weather = newWeather
                }
                break
            case "foggy":
                fogStrength.value = 0.4
                sliderFogStrength.values = [fogStrength.value]
                newWeather = new FoggyWeather({
                    fogStrength: fogStrength.value,
                })
                view.environment.weather = newWeather
                if (viewRight) {
                    viewRight.environment.weather = newWeather
                }
                break
            case "snowy":
                cloudCover.value = 0.5
                sliderCloudCover.values = [cloudCover.value]
                precipitation.value = 0.3
                sliderPrecipitation.values = [precipitation.value]
                newWeather = new SnowyWeather({
                    cloudCover: cloudCover.value,
                    precipitation: precipitation.value,
                })
                view.environment.weather = newWeather
                if (viewRight) {
                    viewRight.environment.weather = newWeather
                }
                break
            default:
                cloudCover.value = 0.2
                sliderCloudCover.values = [cloudCover.value]
                newWeather = new SunnyWeather({
                    cloudCover: cloudCover.value,
                })
                view.environment.weather = newWeather
                if (viewRight) {
                    viewRight.environment.weather = newWeather
                }
        }
    }
}

function setWeatherProperty(
    property: "cloudCover" | "precipitation" | "fogStrength" | "snowCover",
    value: number | "enabled" | "disabled",
) {
    if (!weather.value) return
    const viewRight = getViewRight()
    if (property === "cloudCover" && typeof value === "number") {
        ;(
            view.environment.weather as
                | SunnyWeather
                | CloudyWeather
                | SnowyWeather
                | RainyWeather
        ).cloudCover = value
        if (viewRight) {
            ;(
                viewRight.environment.weather as
                    | SunnyWeather
                    | CloudyWeather
                    | SnowyWeather
                    | RainyWeather
            ).cloudCover = value
        }
    } else if (property === "precipitation" && typeof value === "number") {
        ;(
            view.environment.weather as RainyWeather | SnowyWeather
        ).precipitation = value
        if (viewRight) {
            ;(
                viewRight.environment.weather as RainyWeather | SnowyWeather
            ).precipitation = value
        }
    } else if (property === "fogStrength" && typeof value === "number") {
        ;(view.environment.weather as FoggyWeather).fogStrength = value
        if (viewRight) {
            ;(viewRight.environment.weather as FoggyWeather).fogStrength = value
        }
    } else if (property === "snowCover" && typeof value === "string") {
        ;(view.environment.weather as SnowyWeather).snowCover = value
        if (viewRight) {
            ;(viewRight.environment.weather as SnowyWeather).snowCover = value
        }
    }
}
</script>
